import React, { useEffect, useRef, useMemo, useState } from 'react';
import * as echarts from 'echarts';
import { FaChartBar, FaCoins } from 'react-icons/fa';
import { MdSwapHoriz } from 'react-icons/md';

const QuizChart = ({ quizData }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const [chartView, setChartView] = useState('activity'); // 'activity' or 'coins'

    // Process the data for the chart
    const chartData = useMemo(() => {
        if (!quizData || quizData.length === 0) {
            return {
                dates: [],
                series: [],
                typeTotals: {},
                coinData: {
                    types: [],
                    series: [],
                    totalCoins: 0,
                    typeCoins: {}
                }
            };
        }

        // Group data by date for activity chart
        const groupedByDate = quizData.reduce((acc, item) => {
            const date = new Date(item.createdAt).toLocaleDateString();

            if (!acc[date]) {
                acc[date] = { total: 0, byType: {} };
            }

            acc[date].total += 1;

            // Track by quiz type
            if (item.type) {
                // Normalize the quiz type
                const normalizedType = item.type.trim().toLowerCase();

                if (!acc[date].byType[normalizedType]) {
                    acc[date].byType[normalizedType] = 0;
                }
                acc[date].byType[normalizedType] += 1;
            }

            return acc;
        }, {});

        // Sort dates
        const sortedDates = Object.keys(groupedByDate).sort((a, b) =>
            new Date(a) - new Date(b)
        );

        // Get quiz types across all data with normalization
        const quizTypes = [...new Set(
            quizData
                .filter(item => item.type)
                .map(item => item.type.trim().toLowerCase())
        )];

        // Calculate totals by type for the summary
        const typeTotals = quizTypes.reduce((acc, type) => {
            acc[type] = quizData.filter(item =>
                item.type && item.type.trim().toLowerCase() === type
            ).length;
            return acc;
        }, {});

        const colorMap = {
            'daily': '#5470c6',       // Blue
            'welcome': '#91cc75',     // Green
            'admin': '#fac858',       // Yellow/Orange
            'ad': '#ee6666',          // Red
            'readwin': '#73c0de',     // Light Blue
            'dailystreak': '#3ba272', // Dark Green
            'jackpot': '#0900ff',     // Blue
            'nonstop': "#f56a00",     // Orange
            'tapwin': '#9b59b6',      // Purple
            'wrongans': '#e74c3c',     // Dark Red
            'whois': '#f72585',     // Black
            'fsad': '#1abc9c',        // Teal
            'rwdad': '#e67e22',       // Carrot Orange
            'ntvad': '#8e44ad',       // Deep Purple
            'inhousad': '#2ecc71',    // Emerald Green
            'game': '#010402',  // black
            'notification': '#ff69b4' // Hot Pink 
        };



        // Create series for each quiz type for activity chart
        const activitySeries = quizTypes.map((type, index) => {
            // Format type name for display (capitalize first letter)
            const displayName = type.charAt(0).toUpperCase() + type.slice(1);

            return {
                name: `${displayName}`,
                type: 'bar',
                stack: 'total',
                emphasis: {
                    focus: 'series'
                },
                data: sortedDates.map(date =>
                    (groupedByDate[date].byType[type] || 0)
                ),
                itemStyle: {
                    color: colorMap[type] || `hsl(${(index * 137) % 360}, 70%, 60%)` // Use color map or generate one
                }
            };
        });

        // Process coin data
        const coinsByType = quizTypes.reduce((acc, type) => {
            const items = quizData.filter(item =>
                item.type && item.type.trim().toLowerCase() === type
            );

            // Sum up coins, considering 'admin' type as deduction
            const coinSum = items.reduce((sum, item) => {
                const coinValue = item.coins || 0;
                // For admin type, treat positive values as deductions
                if (type === 'admin') {
                    return sum - Math.abs(coinValue); // Deduct the absolute value
                }
                return sum + coinValue;
            }, 0);

            acc[type] = coinSum;
            return acc;
        }, {});

        // Calculate total coins awarded
        const totalCoins = Object.values(coinsByType).reduce((sum, val) => sum + val, 0);

        // Prepare coin pie chart data
        const coinPieData = Object.entries(coinsByType).map(([type, coins]) => {
            const displayName = type.charAt(0).toUpperCase() + type.slice(1);
            const isNegative = coins < 0;

            return {
                name: `${displayName} ${isNegative ? '(Deducted)' : ''}`,
                value: Math.abs(coins), // Use absolute value for display
                itemStyle: {
                    color: isNegative ? '#e74c3c' : colorMap[type] || `hsl(${(quizTypes.indexOf(type) * 137) % 360}, 70%, 60%)`
                },
                isNegative: isNegative // Add flag for negative values
            };
        });

        return {
            dates: sortedDates,
            series: activitySeries,
            typeTotals,
            coinData: {
                types: quizTypes,
                series: coinPieData,
                totalCoins,
                typeCoins: coinsByType
            }
        };
    }, [quizData]);

    // Initialize and update chart
    useEffect(() => {
        const initChart = () => {
            if (chartRef.current) {
                // Dispose of existing chart instance if it exists
                if (chartInstance.current) {
                    chartInstance.current.dispose();
                }

                // Create new chart instance
                chartInstance.current = echarts.init(chartRef.current, null, {
                    renderer: 'canvas'
                });

                // Set chart options based on current view
                if (chartView === 'activity') {
                    // Activity Bar Chart
                    const option = {
                        title: {
                            text: 'Quiz Activity',
                            left: 'center',
                            show: false
                        },
                        tooltip: {
                            trigger: 'axis',
                            axisPointer: {
                                type: 'shadow'
                            },
                            formatter: function (params) {
                                let tooltip = `<div style="margin: 0px 0 0; line-height: 1;"><div style="font-size:14px; color:#666; font-weight:400; line-height: 1;">${params[0].name}</div></div>`;

                                let total = 0;
                                params.forEach(param => {
                                    total += param.value;
                                    tooltip += `<div style="margin: 10px 0 0; line-height: 1;">
                      <div style="margin: 0px 0 0; line-height: 1;">
                        <div style="margin: 0px 0 0; line-height: 1;">
                          <span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${param.color};"></span>
                          <span style="font-size:14px;color:#666;font-weight:400;margin-left:2px">${param.seriesName}</span>
                          <span style="float:right;margin-left:20px;font-size:14px;color:#666;font-weight:900">${param.value}</span>
                          <div style="clear:both"></div>
                        </div>
                      </div>
                    </div>`;
                                });

                                tooltip += `<div style="margin: 10px 0 0; line-height: 1;">
                    <div style="margin: 0px 0 0; line-height: 1;">
                      <div style="margin: 0px 0 0; line-height: 1;">
                        <span style="font-size:14px;color:#666;font-weight:400;margin-left:2px">Total</span>
                        <span style="float:right;margin-left:20px;font-size:14px;color:#666;font-weight:900">${total}</span>
                        <div style="clear:both"></div>
                      </div>
                    </div>
                  </div>`;

                                return tooltip;
                            }
                        },
                        legend: {
                            data: chartData.series.map(series => series.name),
                            top: 'top',
                            type: 'scroll',
                            textStyle: {
                                color: '#666'
                            },
                            selected: {}, // All series visible by default
                            selectedMode: 'multiple' // Allow selecting multiple series
                        },
                        grid: {
                            left: '3%',
                            right: '4%',
                            bottom: '3%',
                            containLabel: true
                        },
                        xAxis: [
                            {
                                type: 'category',
                                data: chartData.dates,
                                axisLabel: {
                                    rotate: chartData.dates.length > 5 ? 45 : 0,
                                    interval: 0,
                                    color: '#666',
                                    formatter: function (value) {
                                        // If there are many dates, format more concisely
                                        if (chartData.dates.length > 10) {
                                            const date = new Date(value);
                                            return `${date.getMonth() + 1}/${date.getDate()}`;
                                        }
                                        return value;
                                    }
                                },
                                axisLine: {
                                    lineStyle: {
                                        color: '#ddd'
                                    }
                                }
                            }
                        ],
                        yAxis: [
                            {
                                type: 'value',
                                minInterval: 1,
                                axisLabel: {
                                    color: '#666'
                                },
                                axisLine: {
                                    show: false
                                },
                                splitLine: {
                                    lineStyle: {
                                        color: '#eee'
                                    }
                                }
                            }
                        ],
                        series: chartData.series,
                        toolbox: {
                            show: true,
                            feature: {
                                saveAsImage: {
                                    show: true,
                                    title: 'Save',
                                    name: 'quiz_activity_chart'
                                },
                                dataView: {
                                    show: true,
                                    title: 'Data',
                                    readOnly: true,
                                    lang: ['Data View', 'Close', 'Refresh']
                                },
                                magicType: {
                                    show: true,
                                    type: ['line', 'bar', 'stack'],
                                    title: {
                                        line: 'Line',
                                        bar: 'Bar',
                                        stack: 'Stack'
                                    }
                                },
                                restore: {
                                    show: true,
                                    title: 'Reset'
                                }
                            },
                            right: '20px'
                        }
                    };

                    // Set option and render chart
                    chartInstance.current.setOption(option);
                } else {
                    // Coin Distribution Pie Chart
                    const option = {
                        title: {
                            text: 'Coin Distribution',
                            left: 'center',
                            show: false
                        },
                        tooltip: {
                            trigger: 'item',
                            formatter: function (params) {
                                const isNegative = params.data.isNegative;
                                const prefix = isNegative ? '-' : '+';
                                return `${params.name}<br/>${prefix} ${params.value} coins (${params.percent}%)`;
                            }
                        },
                        legend: {
                            type: 'scroll',
                            orient: 'vertical',
                            right: 10,
                            top: 20,
                            bottom: 20,
                            textStyle: {
                                color: '#666'
                            }
                        },
                        series: [
                            {
                                name: 'Coin Distribution',
                                type: 'pie',
                                radius: ['40%', '70%'],
                                avoidLabelOverlap: false,
                                itemStyle: {
                                    borderRadius: 10,
                                    borderColor: '#fff',
                                    borderWidth: 2
                                },
                                label: {
                                    show: false,
                                    position: 'center'
                                },
                                emphasis: {
                                    label: {
                                        show: true,
                                        fontSize: '18',
                                        fontWeight: 'bold'
                                    }
                                },
                                labelLine: {
                                    show: false
                                },
                                data: chartData.coinData.series
                            }
                        ]
                    };

                    // Set option and render chart
                    chartInstance.current.setOption(option);
                }
            }
        };

        // Initialize chart when component mounts or data changes
        initChart();

        // Handle resize
        const handleResize = () => {
            if (chartInstance.current) {
                chartInstance.current.resize();
            }
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            if (chartInstance.current) {
                chartInstance.current.dispose();
                chartInstance.current = null;
            }
        };
    }, [chartData, chartView]);

    // Toggle between activity and coin views
    const toggleChartView = () => {
        setChartView(prev => prev === 'activity' ? 'coins' : 'activity');
    };

    // Format large numbers with commas
    const formatNumber = (num) => {
        return new Intl.NumberFormat().format(num);
    };

    // Generate type badges for summary view
    const renderTypeBadges = () => {
        if (chartView === 'activity') {
            if (!chartData.typeTotals || Object.keys(chartData.typeTotals).length === 0) {
                return null;
            }

            return (
                <div className="mt-4 flex flex-wrap gap-2">
                    {Object.entries(chartData.typeTotals).map(([type, count]) => {
                        // Format type name for display (capitalize first letter)
                        const displayName = type.charAt(0).toUpperCase() + type.slice(1);

                        // Get color based on series data
                        const seriesItem = chartData.series.find(s => s.name === displayName);
                        const bgColor = seriesItem ? seriesItem.itemStyle.color : '#888';

                        return (
                            <div
                                key={type}
                                className="px-3 py-1 rounded-full text-xs font-medium text-white flex items-center"
                                style={{ backgroundColor: bgColor }}
                            >
                                {displayName}: {count}
                            </div>
                        );
                    })}
                </div>
            );
        } else {
            // Coin distribution badges
            if (!chartData.coinData || !chartData.coinData.typeCoins || Object.keys(chartData.coinData.typeCoins).length === 0) {
                return null;
            }

            return (
                <div className="mt-4 flex flex-col gap-2">
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                        <h3 className="text-sm font-semibold mb-2 text-blue-700 flex items-center">
                            <FaCoins className="mr-1" /> Total Coin Balance: <span className="ml-2 text-blue-800">{formatNumber(chartData.coinData.totalCoins)} coins</span>
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {Object.entries(chartData.coinData.typeCoins).map(([type, coins]) => {
                                // Format type name for display (capitalize first letter)
                                const displayName = type.charAt(0).toUpperCase() + type.slice(1);
                                const isNegative = coins < 0;

                                // Get color based on coins (positive/negative)
                                const pieItem = chartData.coinData.series.find(s =>
                                    s.name.toLowerCase().includes(type.toLowerCase())
                                );
                                const bgColor = pieItem ? pieItem.itemStyle.color : '#888';

                                return (
                                    <div
                                        key={type}
                                        className={`flex justify-between items-center px-3 py-2 rounded-md ${isNegative ? 'bg-red-50 border border-red-100' : 'bg-green-50 border border-green-100'}`}
                                    >
                                        <span className="text-xs font-medium flex items-center">
                                            <span className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: bgColor }}></span>
                                            {displayName}:
                                        </span>
                                        <span className={`text-xs font-bold ${isNegative ? 'text-red-600' : 'text-green-600'}`}>
                                            {isNegative ? '-' : '+'}{formatNumber(Math.abs(coins))}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            );
        }
    };

    return (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 h-full">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                    <div className={`${chartView === 'activity' ? 'bg-blue-600' : 'bg-amber-500'} p-2 rounded-lg shadow-md text-white mr-3`}>
                        {chartView === 'activity' ? <FaChartBar size={20} /> : <FaCoins size={20} />}
                    </div>
                    <h2 className="text-xl font-semibold text-gray-800">
                        {chartView === 'activity' ? 'Quiz Activity' : 'Coin Distribution'}
                    </h2>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={toggleChartView}
                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded-full text-xs font-medium flex items-center transition-colors duration-200"
                    >
                        <MdSwapHoriz className="mr-1" />
                        Switch to {chartView === 'activity' ? 'Coins' : 'Activity'}
                    </button>
                    <div className="text-sm text-gray-600">
                        {chartView === 'activity' ? (
                            <span><span className="font-semibold">{quizData?.length || 0}</span> quizzes</span>
                        ) : (
                            <span><span className="font-semibold">{formatNumber(chartData.coinData.totalCoins || 0)}</span> coins</span>
                        )}
                    </div>
                </div>
            </div>

            {quizData && quizData.length > 0 ? (
                <>
                    <div ref={chartRef} className="h-[300px] w-full" />
                    {renderTypeBadges()}
                </>
            ) : (
                <div className="h-[300px] flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-gray-500">No quiz data available for the selected period</p>
                </div>
            )}
        </div>
    );
};

export default QuizChart;